/**
 * Papercut waste quantification JSON (Papercut.timeWaste / materialWaste / capitalWaste).
 * Output is always cost in dollars; inputs live in `calculation` only.
 */

export const PAPERCUT_WASTE_QUANTIFICATION_VERSION = 1 as const;

/** Default fully-loaded labor rate ($/hr) — UI default and legacy backfill rate. */
export const DEFAULT_PAPERCUT_LABOR_RATE_DOLLARS_PER_HOUR = 75;

export interface PapercutWasteCostOutput {
  totalCostDollars: number;
}

export interface PapercutWasteQuantificationEnvelope<TCalculation> {
  version: typeof PAPERCUT_WASTE_QUANTIFICATION_VERSION;
  output: PapercutWasteCostOutput;
  calculation: TCalculation;
}

export type PapercutTimeWasteMethod = 'direct' | 'composed';

export interface PapercutTimeWasteDirectCalculation {
  method: 'direct';
  minutesPerOccurrence: number;
  reachPeopleCount: number;
  laborRateDollarsPerHour: number;
  /** How many times per year this labor happens — pairs with Frequency / impact score */
  occurrencesPerYear: number;
  note?: string;
}

export interface PapercutTimeWasteComposedLine {
  label?: string;
  minutes: number;
  multiplier: number;
}

export interface PapercutTimeWasteComposedCalculation {
  method: 'composed';
  description?: string;
  lines: PapercutTimeWasteComposedLine[];
  reachPeopleCount: number;
  laborRateDollarsPerHour: number;
  /** How many times per year this labor happens — pairs with Frequency / impact score */
  occurrencesPerYear: number;
}

export type PapercutTimeWasteCalculation =
  | PapercutTimeWasteDirectCalculation
  | PapercutTimeWasteComposedCalculation;

export type PapercutTimeWaste = PapercutWasteQuantificationEnvelope<PapercutTimeWasteCalculation>;

export type PapercutMaterialWasteMethod = 'none' | 'direct' | 'line_items';

export interface PapercutMaterialWasteNoneCalculation {
  method: 'none';
}

export interface PapercutMaterialWasteDirectCalculation {
  method: 'direct';
  dollars: number;
  /** How many times per year this material waste happens */
  occurrencesPerYear: number;
  note?: string;
}

export interface PapercutMaterialWasteLineItem {
  label?: string;
  quantity: number;
  unitCostDollars: number;
}

export interface PapercutMaterialWasteLineItemsCalculation {
  method: 'line_items';
  lineItems: PapercutMaterialWasteLineItem[];
  /** How many times per year this material waste happens */
  occurrencesPerYear: number;
}

export type PapercutMaterialWasteCalculation =
  | PapercutMaterialWasteNoneCalculation
  | PapercutMaterialWasteDirectCalculation
  | PapercutMaterialWasteLineItemsCalculation;

export type PapercutMaterialWaste = PapercutWasteQuantificationEnvelope<PapercutMaterialWasteCalculation>;

export type PapercutCapitalWasteMethod =
  | 'none'
  | 'direct'
  | 'downtime'
  | 'life_shortened'
  | 'opportunity_revenue';

export interface PapercutCapitalWasteNoneCalculation {
  method: 'none';
}

export interface PapercutCapitalWasteDirectCalculation {
  method: 'direct';
  dollars: number;
  /** How many times per year this capital cost occurs */
  occurrencesPerYear: number;
  note?: string;
}

export interface PapercutCapitalDowntimeCalculation {
  method: 'downtime';
  assetLabel?: string;
  assetValueDollars: number;
  utilizationRate: number;
  productiveHoursPerYear: number;
  hoursDown: number;
  /** How many downtime events per year */
  occurrencesPerYear: number;
}

export interface PapercutCapitalLifeShortenedCalculation {
  method: 'life_shortened';
  assetLabel?: string;
  replacementCostDollars: number;
  expectedLifeYears: number;
  lifeImpactYears: number;
}

export interface PapercutCapitalOpportunityRevenueCalculation {
  method: 'opportunity_revenue';
  assetLabel?: string;
  revenuePerMonthDollars?: number;
  monthsUnavailable?: number;
  /** Legacy day-based inputs — still supported for existing JSON */
  revenuePerDayDollars?: number;
  daysUnavailable?: number;
}

export type PapercutCapitalWasteCalculation =
  | PapercutCapitalWasteNoneCalculation
  | PapercutCapitalWasteDirectCalculation
  | PapercutCapitalDowntimeCalculation
  | PapercutCapitalLifeShortenedCalculation
  | PapercutCapitalOpportunityRevenueCalculation;

export type PapercutCapitalWaste = PapercutWasteQuantificationEnvelope<PapercutCapitalWasteCalculation>;

export interface PapercutTotalWasteCostPerOccurrence {
  timeDollars: number;
  materialDollars: number;
  capitalDollars: number;
  totalDollars: number;
}

/** Annual waste cost — sum of each category's per-occurrence cost × its own frequency. */
export interface PapercutTotalWasteCostPerYear {
  timeDollars: number;
  materialDollars: number;
  capitalDollars: number;
  totalDollars: number;
}
